import { useEffect } from 'react';
import { createPortal } from 'react-dom';

type ToastType = 'success' | 'error';

type ToastProps = {
  showToast: boolean;
  setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
  text?: string;
  type?: ToastType;
  position?: string;
};

export function Toast({
  text = 'Changes successfully saved!',
  showToast,
  setShowToast,
  type = 'success',
  position = 'left-[125px] 2xl:left-[265px]'
}: ToastProps) {
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast, setShowToast]);

  const isError = type === 'error';

  if (!showToast) return null;

  return createPortal(
    <div
      className={`absolute ${position} bottom-16 z-50 flex gap-4 items-center justify-between min-w-96 max-w-2xl h-14 p-4 rounded-lg shadow-lg ${
        isError ? 'bg-rose-50' : 'bg-cyan-50'
      }`}
    >
      <div className="flex items-center gap-4 text-sm text-gray-950 font-medium">
        {isError ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z"
              stroke="#F43F5E"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
              stroke="#0DBC9B"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {text}
      </div>
      <button
        id="toast-close-button"
        data-testid="toast-close-button"
        onClick={() => setShowToast(false)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.29289 4.29289C4.68342 3.90237 5.31658 3.90237 5.70711 4.29289L10 8.58579L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L11.4142 10L15.7071 14.2929C16.0976 14.6834 16.0976 15.3166 15.7071 15.7071C15.3166 16.0976 14.6834 16.0976 14.2929 15.7071L10 11.4142L5.70711 15.7071C5.31658 16.0976 4.68342 16.0976 4.29289 15.7071C3.90237 15.3166 3.90237 14.6834 4.29289 14.2929L8.58579 10L4.29289 5.70711C3.90237 5.31658 3.90237 4.68342 4.29289 4.29289Z"
            fill="#BABBBF"
          />
        </svg>
      </button>
    </div>,
    document.body
  );
}
